import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BigNumber } from 'bignumber.js';
import useWeb3ModalWithCallback from 'app/helpers/useWeb3ModalWithCallback';
import {
  DsButton,
  DsInputString,
  DsExplorerLink,
  DsLogo,
  DsEthAvatar,
  DsForm,
  DsTag,
  DsInputBignumber,
} from '@tokenyico/design-system/dist/reactJS';
import {
  vWalletAddress,
  vBigNumber,
  vMaxBigNumber,
  vGreaterThanBigNumber,
} from '@tokenyico/design-system';
import { dsConfirm } from '@tokenyico/design-system';
import { get, omit } from 'lodash';
import { useHistory } from 'react-router-dom';
import { TokenService, HolderService, DFNSService } from 'services';
import { getApproveDirectContent } from './Confirmation';

const DirectTransferToken = ({
  balance,
  setSuccess,
  onCloseModal,
  forwardedAmountRef,
  forwardedWalletRef,
  token,
  setError,
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tokenQuantity, setTokenQuantity] = useState(null);
  const [recipient, setRecipient] = useState(null);
  const [investor, setInvestor] = useState(null);
  const [errorSubmit, setErrorSubmit] = useState();
  const [errorWeb3, setErrorWeb3] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();

  const conditionalTransfersEnabled = get(token, 'conditionalTransfersEnabled', false);
  const sender = get(balance, 'holder.details.wallet');
  const provider = get(balance, 'holder.details.provider');
  const tokenId = get(balance, 'token.id');
  const symbol = get(balance, 'token.symbol');
  const tokenDecimals = get(balance, 'token.decimals', 0);
  const availableBalance = get(balance, 'unfrozenBalance', 0);

  const { callContract, onConnectWallet, isWeb3ModalOpen, account } = useWeb3ModalWithCallback(
    async () => {
      if (tokenQuantity) onSubmit();
    },
  );

  const handleOnChangeRecipient = async (detail) => {
    setInvestor(null);
    setRecipient(detail);
    setErrorSubmit(null);
    if (!vWalletAddress(detail)) {
      setIsLoading(true);
      const getInvestor = await HolderService.getHolderByWallet(token.id, detail)
        .then((res) => res.investor)
        .catch(() => '');
      setInvestor(getInvestor);
      setIsLoading(true);
    } else {
      setInvestor(null);
    }
    return detail;
  };

  const onConfirmSubmit = async () => {
    const values = { tokenQuantity, symbol, sender, recipient, investor, token };

    if (
      await dsConfirm({
        heading: `${t('shared.transferTokens')}?`,
        content: getApproveDirectContent(t, values),
        action: `${t('shared.yes')}, ${t('shared.transferTokens').toLocaleLowerCase()}`,
        version: 'success',
      })
    ) {
      onSubmit();
    }
  };

  const catchWeb3Error = (res) => {
    const error = res.details || res.message; // res.messages is too long to display.
    setErrorWeb3(error);
    setIsSubmitting(false);
  };

  const onSubmit = async () => {
    setErrorSubmit(null);
    setErrorWeb3(null);
    setIsSubmitting(true);

    const data = {
      tokenQuantity: tokenQuantity,
      ethereumNetwork: token.blockchainNetwork.toUpperCase(),
      tokenAddress: get(balance, 'token.address'),
      senderWallet: sender,
      recipientWallet: recipient,
    };

    if (provider === 'INTEGRATED') {
      try {
        await DFNSService.transfer(token, data);
        setSuccess(t('shared.transactionBroadcasted'));
        handleCloseModal();
      } catch (e) {
        setError(e);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    try {
      await TokenService.postValidateTransfer(tokenId, data);
      if (provider === 'ONCHAINID') {
        // If provider == onchainid && conditionalTransferEnabled =  true
        if (conditionalTransfersEnabled) {
          alert('Request not supported!');
          // TODO: don’t trigger anything. This will be implemented later.
        } else {
          // trigger SP-2312: Simple transfer initialization endpoint
          try {
            await TokenService.postInitSimpleTransfer(tokenId, omit(data, 'tokenAddress'));
          } catch (e) {
            catchWeb3Error(e);
          } finally {
            setSuccess(t('shared.transactionBroadcasted'));
            handleCloseModal();
          }
        }
      } else {
        const isWalletConnected = onConnectWallet();
        if (!isWalletConnected) {
          setIsSubmitting(false);
          return;
        }
        // if wallet provider != onchainid
        // Trigger transfer as current implementation, either for direct or conditional
        const body = { ...data, amount: data.tokenQuantity };
        if (conditionalTransfersEnabled) {
          return TokenService.postConditionalTransfer(
            tokenId,
            omit(body, 'tokenAddress', 'tokenQuantity'),
          )
            .then((res) =>
              handleAllowance({
                tokenAddress: data.tokenAddress,
                fromAddress: data.senderWallet,
                toAddress: get(res, 'params.spender'),
                amount: get(res, 'params.increaseAllowanceBy'),
                decimals: tokenDecimals,
              }),
            )
            .catch((res) => {
              catchWeb3Error(res);
            });
        } else {
          return handleTransferToken({
            tokenAddress: data.tokenAddress,
            fromAddress: data.senderWallet,
            toAddress: data.recipientWallet,
            amount: data.tokenQuantity,
            decimals: tokenDecimals,
          }).catch((res) => {
            catchWeb3Error(res);
          });
        }
      }
    } catch (e) {
      setIsSubmitting(false);
      setErrorSubmit(e.message);
    }
  };

  const handleCloseModal = () => {
    setIsSubmitting(false);
    setRecipient(null);
    setInvestor(null);
    setTokenQuantity(null);
    setErrorSubmit(null);
    setErrorWeb3(null);
    onCloseModal();
  };

  const handleTransferToken = async (data) => {
    return callContract('transfer', data).then(() => {
      setIsSubmitting(false);
      setSuccess(t('shared.transactionBroadcasted'));
      handleCloseModal();
    });
  };

  const handleAllowance = async (data) => {
    const amount = new BigNumber(data.amount);

    // do nothing
    if (amount.isEqualTo(0)) {
      alert('Transfer submitted');
      handleRedirect();
    }

    if (account.isConnected) {
      if (amount.isGreaterThan(0)) {
        return callContract('increaseAllowance', data).then(() => handleRedirect());
      } else {
        // convert negative number into positive string  "-100" => "100"
        data.amount = amount.abs().toString();
        return callContract('decreaseAllowance', data).then(() => handleRedirect());
      }
    } else {
      setIsSubmitting(false);
    }
  };

  const handleRedirect = () => {
    setSuccess(t('shared.transactionBroadcastedConditional'), 10000);
    history.push('/requests');
  };

  return (
    <>
      <DsForm onSubmit={onConfirmSubmit} id="transfer-token">
        <div className="_flex:column _text:sm _p:x(4)">
          <div className="_m:bottom(5) _m:top(3)">
            <div className="_text(500) _m:bottom(1) _flex:align(center)">
              {t('shared.your')}{' '}
              {provider === 'ONCHAINID' && (
                <DsLogo
                  name="onchainid"
                  style={{
                    width: '5rem',
                    margin: '0 0.3rem',
                  }}
                />
              )}{' '}
              {t('shared.wallet').toLocaleLowerCase()}
            </div>
            <div className="_text:xs _text-grey-900 _m:bottom(2)">
              {t('shared.directSenderWalletDesc')}
            </div>
            <DsExplorerLink network={token.blockchainNetwork} address={sender} type="wallet" />
          </div>

          <div className="_m:bottom(4)">
            <div className="_text(500) _m:bottom(1)">{t('shared.recipient')} *</div>
            <div className="_text:xs _text-grey-900 _m:bottom(2)">
              {t('shared.recipientHelperText')}
            </div>
            <DsInputString
              isFloatLabel={true}
              name="recipient"
              type="text"
              placeholder={
                t('shared.recipient') + ' ' + t('shared.walletAddress').toLocaleLowerCase()
              }
              value={recipient}
              ref={forwardedWalletRef}
              onDsInput={({ detail }) => handleOnChangeRecipient(detail)}
              validate={vWalletAddress}
            />

            {investor && isLoading && (
              <span>
                <span className="_text-grey-900">{t('shared.name')}:</span>
                <span className="_m:left(2)">
                  <DsEthAvatar size="12px" address={recipient} /> {investor}
                </span>
              </span>
            )}
          </div>

          <div className="_m:bottom(4)">
            <div className="_text(500) _m:bottom(1)">{t('shared.tokensToSend')} *</div>
            <div className="_text:xs _text-grey-900 _m:bottom(2)">
              {t('shared.tokensToSendHelperText')}
            </div>
            <DsInputBignumber
              isFloatLabel={true}
              name="tokenQuantity"
              type="text"
              ref={forwardedAmountRef}
              value={tokenQuantity}
              placeholder={t('shared.tokenQuantityToSend')}
              onDsInput={({ detail }) => setTokenQuantity(detail)}
              symbol={token.symbol}
              validate={(v) =>
                vBigNumber(tokenDecimals)(v) ||
                vGreaterThanBigNumber(0)(v) ||
                vMaxBigNumber(availableBalance)(v)
              }
            />
          </div>

          {errorSubmit && (
            <div className="help-error">
              <div className="_align:center">{errorSubmit}</div>
            </div>
          )}

          {errorWeb3 && (
            <div className="help-error">
              <div className="_align:center">{errorWeb3}</div>
            </div>
          )}

          {conditionalTransfersEnabled && (
            <DsTag
              label={t('shared.transferTokenDescriptionConditional')}
              isSquare="true"
              size="sm"
              icon="info"
              iconSize="14px"
              className="_m:bottom(4)"
            />
          )}

          <div className="_flex:row _m:top(4) _flex:justify(end)">
            <DsButton
              version="monochrome"
              label={t('shared.cancel')}
              type="button"
              onClick={() => handleCloseModal()}
            />

            <DsButton
              version="primary"
              label={t('shared.transferTokens')}
              type="submit"
              className="_m:left(2)"
              icon="checkmark"
              isReverse
              isLoading={isSubmitting || isWeb3ModalOpen}
            />
          </div>
        </div>
      </DsForm>
    </>
  );
};

export default DirectTransferToken;
